import api from '@api';

/**
 * Fetches the list of all accessible Google Analytics properties.
 * @param {Object} Object.configs - Additional request configs.
 * @param {Object} Object - Options.
 * @param {string} Object.token - Auth token.
 * @returns {Promise<Array<Object>>} An array of GA4 property objects.
 */
export const fetchProperties = async ({ token, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.get(
      '/v2/admin/integration/ga-analytics/properties',
      config,
    );
    return response.data?.data?.ga4Properties ?? null;
  } catch (error) {
    console.error(
      'API Error fetching properties:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        'Failed to fetch properties',
    );
  }
};

/**
 * Fetches report data from Google Analytics 4.
 * @param {Object} Object - Options.
 * @param {string} Object.token - Auth token.
 * @param {Object} Object.filter - The GA4 Reporting filters.
 * @param {Object} Object.configs - Additional request configs.
 * @param {string} Object.filter.propertyId - The GA4 Property ID.
 * @param {Array<Object>} Object.filter.dateRanges - Date ranges for the report.
 * @param {Array<Object>} Object.filter.dimensions - List of dimensions.
 * @param {Array<Object>} Object.filter.metrics - List of metrics.
 * @param {Array<Object>} [Object.filter.dimensionFilter] - Optional dimension filters.
 * @returns {Promise<Object>} The raw report data from GA4.
 */
export const fetchReportData = async ({ token, filter, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.post(
      '/v2/admin/integration/ga-analytics/report',
      filter,
      config,
    );
    return response.data?.data ?? null;
  } catch (error) {
    console.error(
      'API Error fetching report data:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        'Failed to fetch report data',
    );
  }
};

/**
 * Fetches metadata for dimensions and metrics available for a given GA4 property.
 * @param {Object} Object - Options.
 * @param {string} Object.token - Auth token.
 * @param {string} Object.propertyId - The GA4 Property ID.
 * @param {Object} Object.configs - Additional request configs.
 * @returns {Promise<Object>} An object containing lists of available dimensions and metrics.
 */
export const fetchMetadata = async ({ token, propertyId, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.get(
      `/v2/admin/integration/ga-analytics/metadata/${propertyId}`,
      config,
    );
    return response.data?.data ?? null;
  } catch (error) {
    console.error(
      'API Error fetching metadata:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        'Failed to fetch metadata',
    );
  }
};

export * from './filters';
